import React from 'react';
import { Trash2, AlertCircle, Calendar, Shield } from 'lucide-react';
import type { HypotheticalViolation } from '../../types/predictions';
import { getViolationByCode } from '../../data/cfr-violations';

interface ScenarioViolationListProps {
  violations: HypotheticalViolation[];
  onRemove: (index: number) => void;
}

/**
 * ScenarioViolationList Component
 *
 * Displays a list of hypothetical violations added to the scenario
 *
 * Features:
 * - Shows CFR code, description, date, BASIC category
 * - Displays OOS status with visual indicator
 * - Remove button for each violation
 * - Empty state when no violations
 * - Summary count
 */
export const ScenarioViolationList: React.FC<ScenarioViolationListProps> = ({
  violations,
  onRemove,
}) => {
  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Empty state
  if (violations.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center mt-6 border-2 border-dashed border-gray-300">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h4 className="text-lg font-semibold text-gray-600 mb-2">No Violations Added</h4>
        <p className="text-sm text-gray-500">
          Add hypothetical violations above to build your what-if scenario
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-md font-semibold text-gray-900">
          Scenario Violations ({violations.length})
        </h4>
        <span className="text-sm text-gray-500">
          {violations.length} {violations.length === 1 ? 'violation' : 'violations'} added
        </span>
      </div>

      {/* Violation List */}
      <div className="space-y-3">
        {violations.map((violation, index) => {
          const violationDetails = getViolationByCode(violation.cfrCode);
          const effectiveSeverity = violation.oos
            ? violation.severity * 3
            : violation.severity;

          return (
            <div
              key={index}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300
                       transition-colors shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Violation Info */}
                <div className="flex-1 min-w-0">
                  {/* CFR Code and Description */}
                  <div className="flex items-start gap-2 mb-2">
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 text-sm">
                        {violation.cfrCode}
                      </h5>
                      <p className="text-sm text-gray-600 mt-1">
                        {violationDetails?.description || 'CFR violation'}
                      </p>
                    </div>

                    {/* OOS Badge */}
                    {violation.oos && (
                      <span className="flex-shrink-0 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded border border-orange-300">
                        OOS
                      </span>
                    )}
                  </div>

                  {/* Metadata Row */}
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                    {/* BASIC Category */}
                    <div className="flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      <span className="font-medium">{violation.basicId}</span>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(violation.violationDate)}</span>
                    </div>

                    {/* Severity */}
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Severity:</span>
                      <span>
                        {violation.severity}
                        {violation.oos && (
                          <span className="text-orange-700 font-semibold">
                            {' '}x3 = {effectiveSeverity.toFixed(1)}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => onRemove(index)}
                  className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg
                           transition-colors border border-transparent hover:border-red-200"
                  title="Remove violation"
                  aria-label="Remove violation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-blue-900 font-medium">Total Violations:</span>
          <span className="text-blue-900 font-bold">{violations.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-blue-900 font-medium">OOS Violations:</span>
          <span className="text-blue-900 font-bold">
            {violations.filter(v => v.oos).length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScenarioViolationList;
